import { Controller, useFormContext } from "react-hook-form";
import { type SxProps, type Theme } from "@mui/material/styles";

import { Box } from "ui/box";
import { Button } from "ui/button";
import { DialogActions } from "ui/dialog";
import { Select, OptionType } from "ui/select";
import { TextField } from "ui/text-field";
import { Switch } from "ui/switch";
import CreativeFormFields from "components/modals/creative-modal/components/creative-form-fields";

import { useParentTouchpointContext } from "components/modals/create-parent-touchpoint-modal/context/parent-touchpoint-context";
import { useCreateParentTouchpointModal } from "components/modals/create-parent-touchpoint-modal/hooks/use-create-parent-touchpoint-modal";

import { PARENT_VALUE } from "components/modals/create-parent-touchpoint-modal/create-parent-touchpoint-modal.constants";
import { getFYOptions } from "utilities";

import Icons from "assets";

const parentPopulationTypeOptions = [{ label: "Parent", value: PARENT_VALUE }];
const CREATE_NEW_PARENT_VALUE = "__create_new_parent__";

const FIELDS_SX: SxProps<Theme> = { display: "flex", flexDirection: "column", gap: 2 };

const ParentTouchpointDetails = () => {
  const { selectedVersion, selectedCampaign, clientCampaigns } = useParentTouchpointContext();
  const fyOptions = getFYOptions();

  const {
    control,
    formState: { errors },
  } = useFormContext();

  const {
    // State
    contentTypes,
    contentTypeOptions,
    classTypeOptions,

    // handlers
    handleStepChange,
    handleCampaignChange,
  } = useCreateParentTouchpointModal();

  const campaignOptions: OptionType[] = [
    ...clientCampaigns.map((campaign) => ({ value: campaign.id, label: campaign.name })),
    { value: CREATE_NEW_PARENT_VALUE, label: "Create campaign..." },
  ];

  const isThemeEnabledInStudentCampaign = selectedVersion?.applyClientTheme ?? false;

  return (
    <Box sx={FIELDS_SX}>
      <Controller
        name="campaignId"
        control={control}
        render={({ field: { value, onChange, ...field } }) => (
          <Select
            {...field}
            label="Campaign name"
            selectName="parent-campaign-select"
            ariaLabel="Choose campaign"
            required
            startIcon={<Icons.Search className="text-aqua-600" />}
            options={campaignOptions}
            value={campaignOptions.find((option) => option.value === value) ?? null}
            onChange={(option) => {
              const selectedValue = String(option?.value ?? "");
              if (selectedValue === CREATE_NEW_PARENT_VALUE) {
                handleStepChange(4);
                return;
              }
              handleCampaignChange(selectedValue, onChange);
            }}
            errMessage={errors.campaignId?.message as string}
          />
        )}
      />
      <Controller
        name="touchpointName"
        control={control}
        render={({ field }) => (
          <TextField
            label="Email name"
            required
            errMessage={errors.touchpointName?.message as string}
            {...field}
          />
        )}
      />
      <CreativeFormFields
        populationTypeOptions={parentPopulationTypeOptions}
        contentTypeOptions={contentTypeOptions}
        classTypeOptions={classTypeOptions}
        fyOptions={fyOptions}
        isPopulationTypeLocked
        isContentTypeLocked={contentTypes?.length === 1}
        resetKey={selectedCampaign.id}>
        <Controller
          name="applyClientTheme"
          control={control}
          render={({ field }) => (
            <Switch
              toggleName="Enable client theme"
              disabled={isThemeEnabledInStudentCampaign}
              checked={field.value}
              onChange={field.onChange}
            />
          )}
        />
      </CreativeFormFields>
    </Box>
  );
};

export const ParentTouchpointDetailsActions = () => {
  const {
    formState: { isValid },
  } = useFormContext();
  const { handleClose, handleStepChange } = useCreateParentTouchpointModal();

  return (
    <DialogActions
      primary={
        <Button size="large" disabled={!isValid} onClick={() => handleStepChange(2)}>
          Continue to prompt
        </Button>
      }
      secondary={
        <Button size="large" variant="outlined" onClick={handleClose}>
          Cancel
        </Button>
      }
    />
  );
};

export default ParentTouchpointDetails;
